import SignInCard from '../components/SignInCard';
import { useEffect, useState } from 'react';
import {
  supabase,
  abandonSupabaseLogin,
  hasRecentGoogleAuthFailure,
} from '../services/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCookie } from '../utils/helpers';
import leftSideSvg from '../assets/images/left side.png';
import {
  getPostAuthHomeOrQuestionnaire,
  readMedicalQuestionnaireCompletedFromStorage,
} from '../utils/signInRedirect';

export default function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    const checkAuthentication = async () => {
      const hasAccessToken = Boolean(getCookie('access') || getCookie('refresh'));
      const userRole = localStorage.getItem('userRole');
      const onBoardingCompleted = localStorage.getItem('on_boarding_completed');
      const onBoardingStep = localStorage.getItem('on_boarding_step');
      const mustChangePassword = localStorage.getItem('must_change_password') === 'true';
      
      if (hasAccessToken && userRole) {
        const needsPersonalDetails =
          onBoardingCompleted === 'false' &&
          String(onBoardingStep || '').trim() === 'Personal Details';
        const needsBarcodeActivation =
          onBoardingCompleted === 'false' &&
          ['bar code', 'barCode'].includes(String(onBoardingStep || '').trim().toLowerCase());
        const onboardingIncomplete = onBoardingCompleted === 'false';
        const needsPasswordChange =
          onboardingIncomplete && mustChangePassword;
        let viewAs = null;
        try {
          viewAs = localStorage.getItem('supervisorViewAsRole');
        } catch (_) {
          // ignore
        }
        const medicalQuestionnaireCompleted =
          readMedicalQuestionnaireCompletedFromStorage();
        const redirectPath = needsPasswordChange
          ? '/onboarding/change-password'
          : needsBarcodeActivation
          ? '/activate-barcode'
          : onboardingIncomplete || needsPersonalDetails
          ? '/personal-information'
          : getPostAuthHomeOrQuestionnaire(
              medicalQuestionnaireCompleted,
              userRole,
              viewAs
            );
        navigate(redirectPath, { replace: true });
        return;
      }

      if (!supabase) {
        setIsCheckingAuth(false);
        return;
      }

      try {
        // A recent Google callback failure must not auto-forward again, even if
        // a local Supabase session reappears (storage race / another tab).
        if (hasRecentGoogleAuthFailure()) {
          setIsCheckingAuth(false);
          return;
        }

        // getUser() hits Supabase. getSession() only reads localStorage and
        // would forward a JWT whose server session no longer exists.
        const { data, error } = await supabase.auth.getUser();
        if (error || !data?.user) {
          await abandonSupabaseLogin();
          setIsCheckingAuth(false);
          return;
        }
        navigate('/auth/callback', { replace: true });
      } catch (error) {
        try {
          await abandonSupabaseLogin();
        } catch (_) {
          // ignore cleanup errors
        }
        setIsCheckingAuth(false);
      }
    };

    checkAuthentication();
  }, [navigate, location]);

  if (isCheckingAuth) {
    return (
      <div className="min-w-screen min-h-screen flex items-center justify-center bg-[#F9F5F2]">
        <div className="flex flex-col items-center justify-center gap-4">
          <div className="w-12 h-12 rounded-full border-4 border-[#119975]/30 border-t-[#119975] animate-spin" />
          <span className="text-[#2B3133] font-poppins text-sm">
            Loading...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white h-auto lg:h-[700px] flex flex-col overflow-hidden overflow-x-hidden relative">
      <div className="w-full max-w-[1920px] mx-auto flex-1 min-h-0 flex flex-col lg:flex-row px-4 sm:px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-40 gap-4 lg:gap-8 items-stretch py-8 md:py-6">
        {/* Left Panel - Marketing/Visual - mirrors onboarding layout */}
        <div className="hidden lg:flex lg:w-1/2 lg:min-h-0 lg:flex-shrink-0 relative overflow-hidden rounded-3xl bg-[#F9F5F2]">
          <div className="absolute inset-0 rounded-3xl overflow-hidden">
            <img
              src={leftSideSvg}
              alt="Feel your best every day"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/20 rounded-3xl" />
          </div>
          <div className="relative z-10 flex flex-col justify-end p-8 md:p-10 lg:p-[50px] w-full min-h-0 flex-1">
            {/* Slogan and Supporting Text - At Bottom */}
            <div className="flex flex-col gap-4 md:gap-5 text-white pb-2 md:pb-3 flex-shrink-0">
              <h1 className="text-3xl md:text-4xl lg:text-[40px] xl:text-[44px] font-libreBaskerville font-bold leading-[1.15]">
                Feel your best every day
              </h1>
              {/* <p className="text-base md:text-lg lg:text-[20px] font-kumbh text-white/90 leading-relaxed">
                Supplements your body truly needs based on real data.
              </p> */}
            </div>
          </div>
        </div>

        {/* Right Panel - Sign In Form - 50% width */}
        <div className="w-full min-w-0 lg:w-1/2 lg:min-h-0 lg:flex-shrink-0 flex flex-col items-center justify-center lg:items-end lg:justify-center overflow-hidden">
          <SignInCard />
        </div>
      </div>
    </div>
  );
}
